/**
 * Done. / ChiroCPD — TopNav Feature Component
 *
 * Desktop: horizontal links. Mobile: hamburger menu.
 * Active state = underline + semibold (NOT colour alone — WCAG 1.4.1).
 * Expired subscription: links are aria-disabled="true" + aria-describedby.
 * Hamburger: aria-expanded, aria-controls, Escape closes, focus returns to trigger,
 *            inert on <main> while open.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useRoute, useLocation } from 'wouter';
import { cx, getInitials, isSubscriptionActive } from '@/lib/utils';
import { api } from '@/lib/api';
import type { SubscriptionStatus } from '@/lib/types';

interface NavLink {
  href: string;
  label: string;
}

const NAV_LINKS: NavLink[] = [
  { href: '/dashboard',  label: 'Dashboard'   },
  { href: '/activities', label: 'Activities'  },
  { href: '/learn',      label: 'Learn'       },
  { href: '/export',     label: 'Export'      },
];

interface TopNavProps {
  subscriptionStatus: SubscriptionStatus;
  subscriptionExpiresAt?: string | null;
  user: { name: string; initials?: string; isAdmin?: boolean };
  currentRoute: string;
}

// ── Route-active helper (wouter) ──────────────────────────────────────────────
function NavLink({
  href,
  label,
  isActive,
  isDisabled,
  onNavigate,
}: {
  href: string;
  label: string;
  isActive: boolean;
  isDisabled: boolean;
  onNavigate?: (() => void) | undefined;
}): React.ReactElement {
  if (isDisabled) {
    return (
      <span
        aria-disabled="true"
        aria-describedby="lockout-reason"
        className="px-1 py-0.5 text-sm font-medium text-[var(--text-muted)] cursor-not-allowed select-none"
      >
        {label}
      </span>
    );
  }

  return (
    <Link
      href={href}
      aria-current={isActive ? 'page' : undefined}
      onClick={onNavigate ? () => { onNavigate(); } : undefined}
      className={cx(
        'px-1 py-0.5 text-sm transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-done-500)] rounded',
        isActive
          ? 'font-semibold text-[var(--color-done-700)] border-b-2 border-[var(--color-done-600)]'
          : 'font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
      )}
    >
      {label}
    </Link>
  );
}

export function TopNav({ subscriptionStatus, subscriptionExpiresAt, user }: TopNavProps): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const [, navigate] = useLocation();
  const active = isSubscriptionActive(subscriptionStatus, subscriptionExpiresAt);
  const initials = user.initials ?? getInitials(user.name);
  const navLinks = user.isAdmin
    ? [...NAV_LINKS, { href: '/admin/modules', label: 'Modules' }, { href: '/admin/users', label: 'Members' }, { href: '/admin/prompts', label: 'Prompts' }]
    : NAV_LINKS;

  // Close on Escape, return focus to trigger
  const handleKeyDown = useCallback((e: KeyboardEvent): void => {
    if (e.key === 'Escape' && menuOpen) {
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    }
  }, [menuOpen]);



  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [handleKeyDown]);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); };
  }, [menuOpen]);

  // Close avatar dropdown on outside click
  useEffect(() => {
    if (!avatarOpen) return;
    function handleClick(e: MouseEvent): void {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); };
  }, [avatarOpen]);

  const handleLogout = useCallback(async (): Promise<void> => {
    setAvatarOpen(false);
    setMenuOpen(false);
    try { await api.post('/api/auth/logout', {}); } catch { /* ignore */ }
    navigate('/');
    window.location.href = '/';
  }, [navigate]);

  // Route checks — wouter hooks must be called unconditionally
  const [isDashboard] = useRoute('/dashboard');
  const [isActivities] = useRoute('/activities');
  const [isLearn] = useRoute('/learn');
  const [isModule] = useRoute('/modules/:id');
  const [isExport] = useRoute('/export');
  const [isAdminModules] = useRoute('/admin/modules');
  const [isAdminModulesNew] = useRoute('/admin/modules/new');
  const [isAdminModulesEdit] = useRoute('/admin/modules/:id/edit');
  const [isAdminUsers] = useRoute('/admin/users');
  const [isAdminUsersNew] = useRoute('/admin/users/new');
  const [isAdminUsersEdit] = useRoute('/admin/users/:id/edit');
  const [isAdminPrompts] = useRoute('/admin/prompts');

  const routeMap: Record<string, boolean> = {
    '/dashboard':     isDashboard,
    '/activities':    isActivities,
    '/learn':         isLearn || isModule,
    '/export':        isExport,
    '/admin/modules': isAdminModules || isAdminModulesNew || isAdminModulesEdit,
    '/admin/users':   isAdminUsers || isAdminUsersNew || isAdminUsersEdit,
    '/admin/prompts': isAdminPrompts,
  };

  return (
    <header
      className="sticky top-0 z-40 bg-[var(--surface-base)] border-b border-[var(--border-base)] shadow-sm"
      data-print-hidden
    >
      {/* Hidden lockout reason for aria-describedby on disabled links */}
      {!active && (
        <span id="lockout-reason" className="sr-only">
          Navigation disabled — subscription inactive. Resubscribe to restore access.
        </span>
      )}

      <div className="mx-auto max-w-5xl px-4 h-14 flex items-center justify-between">
        {/* Brand */}
        <Link
          href="/dashboard"
          className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--stitch-primary)] rounded"
        >
          <span className="material-symbols-outlined text-[24px] text-[var(--stitch-primary)]">clinical_notes</span>
          <span className="stitch-font-display text-[20px] font-bold tracking-tighter text-[var(--stitch-primary)] uppercase">
            Done. CPD
          </span>
        </Link>

        {/* Desktop nav */}
        <nav
          aria-label="Main navigation"
          className="hidden sm:flex items-center gap-6"
        >
          {navLinks.map(link => (
            <NavLink
              key={link.href}
              href={link.href}
              label={link.label}
              isActive={routeMap[link.href] ?? false}
              isDisabled={!active}
            />
          ))}
        </nav>

        {/* User avatar + mobile hamburger */}
        <div className="flex items-center gap-3">
          {/* Avatar dropdown */}
          <div className="relative" ref={avatarRef}>
            <button
              type="button"
              onClick={() => { setAvatarOpen(o => !o); }}
              aria-label="Account menu"
              aria-expanded={avatarOpen}
              className="h-8 w-8 rounded-full bg-[var(--color-done-100)] text-[var(--color-done-700)] text-sm font-semibold flex items-center justify-center select-none hover:ring-2 hover:ring-[var(--color-done-400)] transition-all"
            >
              {initials}
            </button>
            {avatarOpen && (
              <div className="absolute right-0 top-10 z-50 w-44 rounded-xl border border-[var(--border-base)] bg-[var(--surface-base)] shadow-lg py-1 text-sm">
                <div className="px-3 py-2 border-b border-[var(--border-subtle)]">
                  <p className="font-semibold text-[var(--text-primary)] truncate">{user.name}</p>
                </div>
                <Link
                  href="/account"
                  onClick={() => { setAvatarOpen(false); }}
                  className="flex items-center gap-2 px-3 py-2 text-[var(--text-secondary)] hover:bg-[var(--surface-subtle)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <span className="material-symbols-outlined text-[16px]">manage_accounts</span>
                  Account
                </Link>
                <button
                  type="button"
                  onClick={() => { void handleLogout(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[var(--text-secondary)] hover:bg-[var(--surface-subtle)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <span className="material-symbols-outlined text-[16px]">logout</span>
                  Log out
                </button>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            ref={menuButtonRef}
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => { setMenuOpen(o => !o); }}
            className="sm:hidden p-2 rounded-[var(--radius-md)] text-[var(--text-secondary)] hover:bg-[var(--surface-muted)] min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-done-500)]"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          id="mobile-menu"
          ref={menuRef}
          className="sm:hidden border-t border-[var(--border-base)] bg-[var(--surface-base)] px-4 py-3"
        >
          <nav aria-label="Mobile navigation" className="flex flex-col gap-1">
            {navLinks.map(link => (
              <div key={link.href} className="py-1">
                <NavLink
                  href={link.href}
                  label={link.label}
                  isActive={routeMap[link.href] ?? false}
                  isDisabled={!active}
                  onNavigate={() => { setMenuOpen(false); }}
                />
              </div>
            ))}
            <div className="border-t border-[var(--border-subtle)] mt-2 pt-2 flex flex-col gap-1">
              <Link
                href="/account"
                onClick={() => { setMenuOpen(false); }}
                className="flex items-center gap-2 px-1 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <span className="material-symbols-outlined text-[16px]">manage_accounts</span>
                Account
              </Link>
              <button
                type="button"
                onClick={() => { void handleLogout(); }}
                className="flex items-center gap-2 px-1 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <span className="material-symbols-outlined text-[16px]">logout</span>
                Log out
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
